import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

// MUI Icons
import ApartmentIcon from "@mui/icons-material/Apartment";
import PersonIcon from "@mui/icons-material/Person";
import BoltIcon from "@mui/icons-material/Bolt";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import ImportExportIcon from "@mui/icons-material/ImportExport";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import DescriptionIcon from "@mui/icons-material/Description";
import TuneIcon from "@mui/icons-material/Tune";

// Modals
import GnaAdmin from "../../Modals/Admin/GnaAdmin/GnaAdmin";
import ChargesContainer from "../../Modals/Admin/Charges/ChargesContainer";
import CompanyUserSelection from "../../Modals/Admin/CompanyUserSelection/CompanyUserSelection";
import OnboardingPopup from "./OnboardingPopup/OnboardingPopup";
import MakerChecker from "../MakerChecker/MakerChecker";
import ConfigurePlatform from "../../Modals/Admin/ConfigurePlatform/ConfigurePlatform";
import {
  ErrorMessage,
  SuccessMessage,
} from "../../../commons/Toast/ErrorMessage";

// Redux & Services
import {
  fetchAdmins,
  fetchCompany,
  fetchMetrices,
} from "../../../Redux/Slices/gnaAdminSlice";
import { adminCompanyAccess } from "../../../Services/GNA/AlterApi";
import { generateNoca } from "../../../Services/Schedule/schedule";

import SuperAdminSidebar from "./SuperAdminSidebar";
import AuditModal from "../../Modals/Admin/AuditModal/AuditModal";
import Styles from "./SuperAdminMainDashboard.module.css";
import { Download } from "@mui/icons-material";
import { handleGetAllCompanyDetails } from "./superAdminHelper";

// ─── Avatar color palette ─────────────────────────────────────────────────────
const AVATAR_COLORS = [
  "#4e79a7",
  "#f28e2b",
  "#e15759",
  "#76b7b2",
  "#59a14f",
  "#edc948",
  "#b07aa1",
  "#ff9da7",
  "#9c755f",
  "#bab0ac",
];
const getAvatarColor = (str = "") => {
  let hash = 0;
  for (let i = 0; i < str.length; i++)
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};
const getInitials = (name = "") =>
  name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || "")
    .join("");

// ─── Status Badge ─────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const map = {
    VERIFIED: { dot: "#34656d", label: "Verified" },
    UN_VERIFIED: { dot: "#f0a417", label: "Un-Verified" },
    REJECTED: { dot: "#ff4c5e", label: "Rejected" },
    HOLD: { dot: "#f0a417", label: "Hold" },
  };
  const cfg = map[status] || { dot: "#a9a8b1", label: "InActive" };
  return (
    <span className={Styles.statusBadge}>
      <span className={Styles.statusDot} style={{ background: cfg.dot }} />
      {cfg.label}
    </span>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const SuperAdminMainDashboard = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const metrics = useSelector((s) => s?.gnaAdminSlice?.fetchMetrices?.data);
  const companies = useSelector((s) => s?.gnaAdminSlice?.fetchCompany?.data);
  const admins = useSelector((s) => s?.gnaAdminSlice?.fetchAdmins?.data);
  const role = useSelector((s) => s?.persistedReducer?.userType);
  const userName = useSelector(
    (s) => s?.persistedReducer?.user?.user?.userName,
  );

  const [adminModal, setAdminModal] = useState(false);
  const [chargesModal, setChargesModal] = useState(false);
  const [selectionModal, setSelectionModal] = useState(false);
  const [onboardModal, setOnboardModal] = useState(false);
  const [configureModal, setConfigureModal] = useState(false);
  const [auditModal, setAuditModal] = useState(false);
  const [preAuditSection, setPreAuditSection] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [pageSize, setPageSize] = useState(8);
  const [page, setPage] = useState(0);
  const isMakerCheckerRole = role === "MAKER" || role === "CHECKER";
  const [activeSection, setActiveSection] = useState(
    isMakerCheckerRole ? "pending" : "dashboard",
  );
  const [selectedAdmins, setSelectedAdmins] = useState([]);
  const [activeCompany, setActiveCompany] = useState({});

  useEffect(() => {
    dispatch(fetchMetrices());
    dispatch(fetchCompany());
    // dispatch(fetchAdmins());
  }, [dispatch]);

  // Reset page when search/filter changes
  useEffect(() => {
    setPage(0);
  }, [search, statusFilter]);

  // ─── Derived data ──────────────────────────────────────────────────────────
  const filteredCompanies = useMemo(() => {
    if (!companies) return [];
    return companies.filter((c) => {
      const q = search.toLowerCase();
      const matchSearch =
        !search ||
        c.companyName?.toLowerCase().includes(q) ||
        String(c.companyId ?? "")
          .toLowerCase()
          .includes(q);
      const matchStatus =
        statusFilter === "ALL" || c.accountStatus === statusFilter;
      return matchSearch && matchStatus;
    });
  }, [search, statusFilter, companies]);

  const paginatedCompanies = useMemo(
    () => filteredCompanies.slice(page * pageSize, page * pageSize + pageSize),
    [filteredCompanies, page, pageSize],
  );

  const totalPages = Math.ceil(filteredCompanies.length / pageSize);

  const getRoleLabel = (roles = []) => {
    if (roles.includes("TRADER")) return "Trader";
    if (roles.includes("BANKING")) return "Discom";
    if (roles.includes("GENERATOR") && roles.includes("CONSUMER"))
      return "Prosumer";
    if (roles.includes("CONSUMER")) return "Consumer";
    if (roles.includes("GENERATOR")) return "Generator";
    return "N/A";
  };

  const handleAdminAssignment = async (data) => {
    const payload = data.map((userId) => ({
      companyId: activeCompany?.companyId,
      adminUserId: userId,
      adminRole: admins?.find((a) => a.userId === userId)?.userType,
    }));
    const res = await adminCompanyAccess(payload);
    if (res?.data?.code === 200) {
      dispatch(fetchCompany());
      setSelectionModal(false);
    } else {
      ErrorMessage(res?.data?.error || "Failed to assign admins");
    }
  };

  const handleNocaDownload = async () => {
    const res = await generateNoca();
    if (res?.data?.code === 200) {
      SuccessMessage(res?.data?.message);
      const blob = new Blob([window.atob(res.data.data)], {
        type: "text/plain",
      });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "transaction_data.txt";
      link.click();
    } else {
      ErrorMessage(res?.data?.message || "NOCA Generation Failed");
    }
  };

  const isMakerChecker = isMakerCheckerRole;

  // ─── Table section ─────────────────────────────────────────────────────────
  const CompanyTable = () => (
    <div className={Styles.tableWrapper}>
      <div className={Styles.tableControls}>
        <div className={Styles.searchBox}>
          <SearchIcon className={Styles.searchIcon} />
          <input
            type="text"
            placeholder="Search company name or ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={Styles.searchInput}
          />
        </div>
        <div className={Styles.tableFilters}>
          {/* <div onClick={() => handleGetAllCompanyDetails()}>
            <Download />
          </div> */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className={Styles.filterSelect}
          >
            <option value="ALL">All Status</option>
            <option value="VERIFIED">Verified</option>
            <option value="UN_VERIFIED">Un-Verified</option>
            <option value="HOLD">Hold</option>
            <option value="REJECTED">Rejected</option>
          </select>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(0);
            }}
            className={Styles.filterSelect}
          >
            {[5, 8, 10, 20].map((n) => (
              <option key={n} value={n}>
                {n} / page
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className={Styles.table}>
        <div className={Styles.tableHead}>
          <div className={Styles.colCompany}>Company Name</div>
          <div className={Styles.colRole}>Role</div>
          <div className={Styles.colState}>State</div>
          <div className={Styles.colDate}>Registered</div>
          <div className={Styles.colStatus}>Status</div>
          <div className={Styles.colBalance}>
            {/* VID Balance (₹) */}
            </div>
          <div className={Styles.colActions}>View Details</div>
        </div>

        <div className={Styles.tableBody}>
          {paginatedCompanies.length === 0 ? (
            <div className={Styles.noData}>
              <SearchIcon style={{ fontSize: "32px", opacity: 0.3 }} />
              <span>No companies found{search ? ` for "${search}"` : ""}</span>
            </div>
          ) : (
            paginatedCompanies.map((company) => {
              const initials = getInitials(company.companyName);
              const avatarBg = getAvatarColor(
                company.companyId || company.companyName,
              );
              return (
                <div className={Styles.tableRow} key={company.companyId}>
                  <div className={Styles.colCompany}>
                    <div
                      className={Styles.avatar}
                      style={{ background: avatarBg }}
                    >
                      {initials}
                    </div>
                    <div className={Styles.companyInfo}>
                      <div className={Styles.companyName}>
                        {company.companyName}
                      </div>
                      <div className={Styles.companyId}>
                        {company.companyId}
                      </div>
                    </div>
                  </div>
                  <div className={Styles.colRole}>
                    {getRoleLabel(company?.roles)}
                  </div>
                  <div className={Styles.colState}>
                    {company?.state || company?.registeredState || "—"}
                  </div>
                  <div className={Styles.colDate}>
                    {company?.registeredAt || "—"}
                  </div>
                  <div className={Styles.colStatus}>
                    <StatusBadge status={company?.accountStatus} />
                  </div>
                  <div className={Styles.colBalance}>
                    {/* {company?.totalEscrowAmount > 0
                      ? `₹${company.totalEscrowAmount.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`
                      : "—"} */}
                  </div>
                  <div className={Styles.colActions}>
                    <button
                      className={Styles.viewDetailsBtn}
                      onClick={() =>
                        navigate(
                          `/admin_dashboard_company?id=${company.companyId}`,
                        )
                      }
                    >
                      <VisibilityIcon style={{ fontSize: "14px" }} />
                      View
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {totalPages > 1 && (
        <div className={Styles.pagination}>
          <span className={Styles.pageInfo}>
            {page * pageSize + 1}–
            {Math.min((page + 1) * pageSize, filteredCompanies.length)} of{" "}
            {filteredCompanies.length}
          </span>
          <div className={Styles.pageButtons}>
            <button
              className={Styles.pageBtn}
              disabled={page === 0}
              onClick={() => setPage((p) => p - 1)}
            >
              ‹
            </button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              const pageNum =
                Math.max(0, Math.min(page - 2, totalPages - 5)) + i;
              return (
                <button
                  key={pageNum}
                  className={`${Styles.pageBtn} ${pageNum === page ? Styles.pageBtnActive : ""}`}
                  onClick={() => setPage(pageNum)}
                >
                  {pageNum + 1}
                </button>
              );
            })}
            <button
              className={Styles.pageBtn}
              disabled={page >= totalPages - 1}
              onClick={() => setPage((p) => p + 1)}
            >
              ›
            </button>
          </div>
        </div>
      )}
    </div>
  );

  // ─── MakerChecker section ──────────────────────────────────────────────────
  const MakerCheckerSection = () => (
    <div className={Styles.makerCheckerWrapper}>
      <div className={Styles.mkTabBar}>
        {[
          { id: "pending", label: "Active", live: true },
          { id: "history", label: "History" },
          { id: "companies", label: "Company List" },
        ].map((tab) => (
          <div
            key={tab.id}
            className={`${Styles.mkTab} ${activeSection === tab.id ? Styles.mkTabActive : ""}`}
            onClick={() => setActiveSection(tab.id)}
          >
            {tab.live && <span className={Styles.liveDot} />}
            {tab.label}
          </div>
        ))}
      </div>
      <div className={Styles.mkContent}>
        {activeSection === "pending" && <MakerChecker type="PENDING" />}
        {activeSection === "history" && <MakerChecker type="HISTORY" />}
        {activeSection === "companies" && CompanyTable()}
      </div>
    </div>
  );

  return (
    <div className={Styles.adminLayout}>
      {/* ── Sidebar ── */}
      <SuperAdminSidebar
        companyCount={companies?.length}
        activeSection={isMakerChecker ? activeSection : activeSection}
        onSectionChange={(sec) => {
          if (sec === "auditlog" && role === "SUPER_ADMIN") {
            setPreAuditSection(activeSection);
            setActiveSection("auditlog");
            setAuditModal(true);
            return;
          }
          setActiveSection(sec);
          if (isMakerChecker) {
            if (sec === "auditlog") setActiveSection("history");
            if (sec === "dashboard") setActiveSection("pending");
          }
        }}
        onChargesClick={() => role === "SUPER_ADMIN" && setChargesModal(true)}
        onConfigureClick={() =>
          role === "SUPER_ADMIN" && setConfigureModal(true)
        }
        onAdminsClick={() => role === "SUPER_ADMIN" && setAdminModal(true)}
      />

      {/* ── Main content ── */}
      <div className={Styles.mainContent}>
        {/* Modals */}
        {configureModal && (
          <ConfigurePlatform
            open={configureModal}
            handleClose={() => setConfigureModal(false)}
          />
        )}
        {chargesModal && (
          <ChargesContainer open handleClose={() => setChargesModal(false)} />
        )}
        {selectionModal && (
          <CompanyUserSelection
            open
            selected={selectedAdmins}
            type="Admin"
            handleSubmit={handleAdminAssignment}
            handleClose={() => setSelectionModal(false)}
            handleSelection={(id) =>
              setSelectedAdmins((prev) =>
                prev.includes(id)
                  ? prev.filter((i) => i !== id)
                  : [...prev, id],
              )
            }
          />
        )}
        {adminModal && (
          <GnaAdmin open handleClose={() => setAdminModal(false)} />
        )}
        {auditModal && (
          <AuditModal
            open={auditModal}
            onClose={() => {
              setAuditModal(false);
              setActiveSection(preAuditSection || "dashboard");
            }}
          />
        )}
        {onboardModal && (
          <OnboardingPopup open handleClose={() => setOnboardModal(false)} />
        )}

        {/* ── Top bar ── */}
        <div className={Styles.topBar}>
          <div className={Styles.topBarLeft}>
            <div className={Styles.breadcrumb}>
              <span className={Styles.breadcrumbLink}>Platform</span>
              <span className={Styles.breadcrumbSep}>/</span>
              <span className={Styles.breadcrumbCurrent}>Dashboard</span>
            </div>
            <h1 className={Styles.pageTitle}>
              {role === "MAKER"
                ? "GNA Maker Dashboard"
                : role === "CHECKER"
                  ? "GNA Checker Dashboard"
                  : "GNA Admin Dashboard"}
            </h1>
          </div>
          <div className={Styles.topBarRight}>
            {/* {role === "SUPER_ADMIN" && (
              <button
                className={Styles.addCompanyBtn}
                onClick={() => setOnboardModal(true)}
              >
                <AddIcon style={{ fontSize: "18px" }} />
                Add Company
              </button>
            )} */}
            <div className={Styles.profileChip}>
              <div className={Styles.profileAvatar}>
                {getInitials(userName || "Super Admin")}
              </div>
              <div className={Styles.profileInfo}>
                <div className={Styles.profileName}>
                  {userName || "Super Admin"}
                </div>
                <div className={Styles.profileRole}>
                  {role === "SUPER_ADMIN"
                    ? "GNA Administrator"
                    : role === "MAKER"
                      ? "GNA Maker"
                      : role === "CHECKER"
                        ? "GNA Checker"
                        : "GNA Admin"}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Metrics row ── */}
        {/* <div className={Styles.metricsRow}>
          {[
            {
              icon: <ApartmentIcon />,
              label: "Companies",
              value: companies?.length ?? 0,
              iconBg: "#fff4ed",
              iconColor: "var(--primary-color-text)",
            },
            {
              icon: <PersonIcon />,
              label: "Consumers",
              value: metrics?.totalConsumers ?? 0,
              iconBg: "#eff9ff",
              iconColor: "#4e79a7",
            },
            {
              icon: <BoltIcon />,
              label: "Generators",
              value: metrics?.totalGenerators ?? 0,
              iconBg: "#f0fff4",
              iconColor: "#34656d",
            },
            {
              icon: <WbSunnyIcon />,
              label: "Discom",
              value: metrics?.totalDiscoms ?? 0,
              iconBg: "#fffbea",
              iconColor: "#f0a417",
            },
            {
              icon: <ImportExportIcon />,
              label: "Traders",
              value: metrics?.totalTraders ?? 0,
              iconBg: "#fff4ed",
              iconColor: "var(--primary-color-text)",
            },
          ].map(({ icon, label, value, iconBg, iconColor }) => (
            <div className={Styles.metricCard} key={label}>
              <div
                className={Styles.metricIcon}
                style={{ background: iconBg, color: iconColor }}
              >
                {icon}
              </div>
              <div className={Styles.metricBody}>
                <div className={Styles.metricLabel}>{label}</div>
                <div className={Styles.metricValue}>{value}</div>
              </div>
            </div>
          ))}
        </div> */}

        {/* ── Action buttons ── */}
        <div className={Styles.actionsRow}>
          <div className={Styles.actionButtons}>
            {/* <div
              className={`${Styles.actionCard} ${Styles.actionCardOrange}`}
              onClick={() => {
                if (role === "SUPER_ADMIN") setAdminModal(true);
                else if (role === "CHECKER") handleNocaDownload();
              }}
              style={{
                cursor:
                  role === "SUPER_ADMIN" || role === "CHECKER"
                    ? "pointer"
                    : "not-allowed",
                opacity:
                  role === "SUPER_ADMIN" || role === "CHECKER" ? 1 : 0.45,
              }}
            >
              <PersonAddAlt1Icon style={{ fontSize: "20px" }} />
              <span>
                {role === "SUPER_ADMIN" ? "GNA Admins" : "Generate NOCA"}
              </span>
            </div> */}

            {/* <div
              className={`${Styles.actionCard} ${Styles.actionCardNeutral}`}
              onClick={() => role === "SUPER_ADMIN" && setChargesModal(true)}
              style={{
                cursor: role === "SUPER_ADMIN" ? "pointer" : "not-allowed",
                opacity: role === "SUPER_ADMIN" ? 1 : 0.45,
              }}
            >
              <DescriptionIcon style={{ fontSize: "20px" }} />
              <span>Charges</span>
            </div>

            <div
              className={`${Styles.actionCard} ${Styles.actionCardBlue}`}
              onClick={() => role === "SUPER_ADMIN" && setConfigureModal(true)}
              style={{
                cursor: role === "SUPER_ADMIN" ? "pointer" : "not-allowed",
                opacity: role === "SUPER_ADMIN" ? 1 : 0.6,
              }}
            >
              <TuneIcon style={{ fontSize: "20px" }} />
              <span>Configure Platform</span>
            </div>

            <div className={Styles.escrowCard}>
              <div className={Styles.escrowLabel}>ESCROW BALANCE (₹)</div>
              <div className={Styles.escrowValue}>
                {metrics?.totalEscrowAmountAvailable != null
                  ? metrics.totalEscrowAmountAvailable.toLocaleString("en-IN", {
                      maximumFractionDigits: 0,
                    })
                  : "—"}
              </div>
            </div> */}
          </div>
        </div>

        {/* ── Table / MakerChecker ── */}
        <div className={Styles.tableSection}>
          {isMakerChecker ? MakerCheckerSection() : CompanyTable()}
        </div>
      </div>
    </div>
  );
};

export default SuperAdminMainDashboard;
